import { inject, observer } from "mobx-react";
import React, { FC } from "react";
import {
  Icon,
  Grid,
  Cell,
  MeetingProgress,
  Text,
  Button,
  Tooltip,
} from "src/components";
import { theme } from "src/config";
import { match } from "react-router";
import { MatchParams } from "src/routes";
import {
  RoutingStore,
  UserStore,
  AuthStore,
  MeetingStore,
  SpaceStore,
  ItemStores
} from "src/stores";
import { UserMenu, Breadcrumb } from "src/views";
import { User } from "src/models";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

interface NavbarProps {
  authStore?: AuthStore;
  itemStores?: ItemStores;
  match?: match<MatchParams>; // This is injected so it has to be called match
  meetingStore?: MeetingStore;
  params?: MatchParams;
  spaceStore?: SpaceStore;
  routingStore?: RoutingStore;
  userStore?: UserStore;
}

interface UserRoleDebuggerProps {
  user: User;
}

const SpaceTitle = styled.span`
  font-weight: 600;
  text-decoration: underline;
`;

const UserRoleDebugger: FC<UserRoleDebuggerProps> = (props) => {
  return (
    <>
      {props.user && (
        <Text size="xxsmall" color={theme.color.black}>
          <strong>Current roles:</strong>
          <br />
          <SpaceTitle>workspace: </SpaceTitle>
          <strong>{props.user.getRole(props.user.workspace_id)}</strong> <br />
          <SpaceTitle>project: </SpaceTitle>
          <strong>{props.user.getRole(props.user.project_id)}</strong>
          <br />
          <SpaceTitle>room: </SpaceTitle>
          <strong>{props.user.getRole(props.user.room_id)}</strong>
        </Text>
      )}
    </>
  );
};

export const NavBar: FC<NavbarProps> = inject(
  "authStore",
  "spaceStore",
  "itemStores",
  "meetingStore",
  "routingStore",
  "userStore"
)(
  observer((props: NavbarProps) => {
    const { authStore, routingStore } = props;
    const { t, i18n } = useTranslation();
    const ref = React.useRef<HTMLDivElement>();

    React.useEffect(() => {
      routingStore.setNavBarHeight(ref.current.offsetHeight + "px");
    });
    
    const meeting = props.meetingStore.getActiveMeeting;

    React.useEffect(() => {
      if(meeting && meeting.agenda_id) {
        const load = async () => await meeting.loadAgenda();
        load();
      }
    }, [meeting]);

    return (
      <NavContainer ref={ref}>
        <Cell>
          <Breadcrumb match={props.match} />
        </Cell>
        <Cell>
          {!!meeting && (
            <MeetingProgress
              title={meeting.title}
              percentage={props.meetingStore.getActiveMeetingProgress}
              duration={meeting.duration}
              opened={meeting.opened}
              prevStep={meeting.getPrevAgendaStep}
              curStep={meeting.getCurrentAgendaStep}
              nextStep={meeting.getNextAgendaStep}
              onPrev={meeting.goToPrevAgendaStep}
              onNext={() => meeting.getNextAgendaStep ? meeting.goToNextAgendaStep() : props.meetingStore.stopMeeting(meeting)}
              onClick={() => props.meetingStore.setActive(meeting)}
            />
          )}
        </Cell>
        <Cell style={{ display: "flex", alignItems: "center" }}>
          {props.spaceStore.getCurRoom && (
            <Tooltip content={t('notification_settings')}>
            <Button  onClick={() => {
              props.spaceStore.toggleSpaceSettingsModal();
            }} variant='icon'>
              <Icon
                size="medium"
                iconId="notification"
              />
            </Button>
            </Tooltip>
          )}
          {props.spaceStore.getCurRoom && (
            <Tooltip content={ props.routingStore.boardDirection == 'vertical' ? t('display_horizontal_view') : t('display_vertical_view')}>
            <Button  onClick={() => {
              props.routingStore.toggleBoardView();
            }} variant='icon'>
              <Icon
                size="large"
                iconId="split"
                direction={props.routingStore.boardDirection}
              />
            </Button>
            </Tooltip>
          )}
          <UserMenu />
        </Cell>
      </NavContainer>
    );
  })
);

const NavContainer = styled(Grid)`
  align-content: center;
  align-items: center;
  justify-content: space-between;
  grid-template-columns: repeat(4, minmax(56px, 80px));
  background-color: transparent;
  display: flex;
  opacity: 1;
  padding: 0.75rem 1rem;
  position: relative;
  transform: translateY(0);
  width: 100%;
  z-index: 2;
`;
